/**
 * The status pill, written once.
 *
 * This is ora's Monitor status badge (the one that read "Input Needed"):
 * amber fill, amber border, rounded-full. Every surface that tells the Servicer
 * a run is waiting on them uses it with the "Needs You" label, in ora and in
 * the Outlook add-in: the Monitor grid, the step-view pill in both drawers,
 * the add-in's current-email strip, endorsement history and the nav count.
 *
 * Why a module. The drawer's step-view pill used to be a filled sunrise
 * capsule with a black outline. That is the exact recipe of the Send button
 * next to it, so the status read as something to click. Sunrise stays
 * reserved for actions; a status is this pill.
 *
 * Import in ora as:   ~/common/workflows/statusPill
 * Import in add-in as: @product-app/common/workflows/statusPill
 */

/** Shape, size and weight shared by every tone. */
export const STATUS_PILL_BASE =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";

/** Colours, each with its own border so every tone is the same size. */
export const STATUS_PILL_TONE = {
  /** Waiting on the Servicer: "Needs You". */
  needsYou: "border border-primary-warning-200 bg-primary-warning-50 text-primary-warning-700",
  /** Not confirmed / finished with warnings: same amber as `needsYou`, its own name so a status is never "Needs You" by accident. */
  warning: "border border-primary-warning-200 bg-primary-warning-50 text-primary-warning-700",
  /** Calm: "Running", "Done", "Pending Email Send". */
  neutral: "border border-primary-gray-light-200 bg-base-white text-primary-gray-light-700",
} as const;

export type StatusPillTone = keyof typeof STATUS_PILL_TONE;

export function statusPillClass(tone: StatusPillTone): string {
  return `${STATUS_PILL_BASE} ${STATUS_PILL_TONE[tone]}`;
}

/**
 * The substrate statuses that mean "waiting on you". `rpa_review` is the
 * review-checkpoint twin of `paused_hitl` (same bucket in the Monitor sort).
 * A status alone; a pill asks `isWaitingOnPerson`, which also knows the one
 * wait no status names (the Servicer's own documents).
 */
export const NEEDS_YOU_STATUSES: ReadonlySet<string> = new Set([
  "paused_hitl",
  "warm_hitl",
  "rpa_review",
]);

export function isNeedsYouStatus(status: string | null | undefined): boolean {
  return status != null && NEEDS_YOU_STATUSES.has(status.toLowerCase());
}

/**
 * What a surface knows about WHO a parked run is waiting on, beyond its status.
 * Pass whatever the payload in hand carries; either is enough.
 *
 * - `hitlQuestion`: the run's parked question (the status payload, a push). On
 *   the carrier-evidence wait the substrate stamps `evidence_source: "am"` on
 *   the AM branch (`closing_tail_tools.apply_evidence_branch`, re-applied by
 *   take-over / revert).
 * - `needsYou`: the server's own verdict on a list / queue / email-state row
 *   (`waiting_queue.is_notifiable`), which reads that same flag.
 */
export interface WaitSignals {
  /** As the payload types it (`unknown` on a snapshot); read defensively. */
  hitlQuestion?: unknown;
  needsYou?: boolean | null;
}

function evidenceSourceOf(hitlQuestion: unknown): unknown {
  return hitlQuestion != null && typeof hitlQuestion === "object"
    ? (hitlQuestion as Record<string, unknown>).evidence_source
    : undefined;
}

/**
 * Is this run waiting on the Servicer — the "Needs You" pill? The ONE derivation
 * every pill uses, in ora and the add-in.
 *
 * Every NEEDS_YOU_STATUSES status, plus `pending_edocs` on the AM branch: a run
 * waiting for the Servicer's OWN documents is Needs You everywhere (story 28633,
 * ruled 2026-10-08). The carrier branch of the same status stays the white
 * "Pending Documents" — see `isWaitingOnCarrierDocuments`.
 */
export function isWaitingOnPerson(
  status: string | null | undefined,
  signals: WaitSignals = {},
): boolean {
  if (isNeedsYouStatus(status)) return true;
  if (status?.toLowerCase() !== "pending_edocs") return false;
  return signals.needsYou === true || evidenceSourceOf(signals.hitlQuestion) === "am";
}

/** `pending_edocs` waiting on the CARRIER's documents: the white "Pending Documents". */
export function isWaitingOnCarrierDocuments(
  status: string | null | undefined,
  signals: WaitSignals = {},
): boolean {
  return status?.toLowerCase() === "pending_edocs" && !isWaitingOnPerson(status, signals);
}
